import { useEffect, useRef } from "react"
import { OIO_STAGES } from "@/features/oio/config/oio.config"
import { useGenerationStore } from "@/features/oio/generation.store"
import type { WorkflowStageDefinition } from "@/types"

/**
 * Drives the officer-facing "live thinking" transcript.
 *
 * Why client-side: the OIO graph runs its per-allegation analysis inside a
 * single `Send`-fan-out node, so the only progress signal that reliably reaches
 * the browser is the node-boundary `completed_stages` / `current_stage` state.
 * Mid-node `copilotkit_emit_state` calls (per analysis sub-stage) do not stream
 * through the LangGraphAGUIAgent + Send path. So we synthesise the transcript
 * from the active stage.
 *
 * The transcript is stored PER CASE in the persisted generation store — not in
 * component state — so leaving a case and coming back (or refreshing) shows the
 * reasoning intact and resumes from where it was, exactly like ChatGPT/Claude
 * keep a conversation's reasoning. Each tick appends the first activity for the
 * current stage that hasn't been shown yet; once a stage is exhausted it holds
 * (the store dedups), and a stage change naturally introduces new lines.
 */
const TICK_MS = 3500
const EMPTY: string[] = []

export interface AllegationLite {
  id: string
  title: string
}

/**
 * Order-type knobs. Every default is the OIO value, so OIO call sites pass
 * nothing. Another order type passes its own stage list (the registry's
 * `stages`, or its workspace config) and, if it has a per-item stage, which
 * stage that is and what an item is called.
 */
export interface LiveThoughtsOptions {
  /** Stage list whose substages feed the transcript. Default: OIO_STAGES. */
  stages?: readonly WorkflowStageDefinition[]
  /** Stage whose lines are one per item instead of its substages. Default: "analysis". */
  perItemStageId?: string
  /** Noun for a per-item line ("Allegation 2 of 5: ..."). Default: "Allegation". */
  itemNoun?: string
}

function truncate(text: string, max = 64): string {
  const t = text.trim().replace(/\s+/g, " ")
  return t.length > max ? `${t.slice(0, max - 1)}…` : t
}

/** The ordered pool of thought lines for a stage. The per-item stage is case-specific. */
function buildStagePool(
  stageId: string | null,
  allegations: AllegationLite[],
  { stages = OIO_STAGES, perItemStageId = "analysis", itemNoun = "Allegation" }: LiveThoughtsOptions,
): string[] {
  if (stageId === perItemStageId && allegations.length > 0) {
    return allegations.map(
      (a, i) => `${itemNoun} ${i + 1} of ${allegations.length}: ${truncate(a.title)}`,
    )
  }
  const stage = stages.find((s) => s.id === stageId)
  return stage?.substages?.length ? stage.substages : ["Working through the case…"]
}

export function useLiveThoughts(
  caseId: string | null,
  activeStageId: string | null,
  isGenerating: boolean,
  allegations: AllegationLite[] = [],
  options: LiveThoughtsOptions = {},
): string[] {
  const appendThought = useGenerationStore((s) => s.appendThought)
  const thoughts = useGenerationStore((s) => (caseId ? s.runs[caseId]?.thoughts : undefined)) ?? EMPTY

  // Live refs so the single stable interval always reads the latest values
  // without being recreated on every render / stage flicker. `options` is
  // usually an inline object literal (new identity every render), so it is
  // read through a ref too rather than being an effect dependency.
  const caseRef = useRef(caseId)
  const stageIdRef = useRef(activeStageId)
  const allegRef = useRef(allegations)
  const optionsRef = useRef(options)
  caseRef.current = caseId
  stageIdRef.current = activeStageId
  allegRef.current = allegations
  optionsRef.current = options

  useEffect(() => {
    if (!isGenerating || !caseId) return

    const tick = () => {
      const id = caseRef.current
      if (!id) return
      const pool = buildStagePool(stageIdRef.current, allegRef.current, optionsRef.current)
      const existing = useGenerationStore.getState().runs[id]?.thoughts ?? []
      // Append the first activity not yet shown — this is what makes the
      // transcript RESUME correctly after a case switch/refresh (the already
      // persisted lines are skipped) instead of restarting from the top.
      const next = pool.find((p) => !existing.includes(p))
      if (next) appendThought(id, next)
    }

    tick() // surface the next activity immediately on (re)entry
    const handle = setInterval(tick, TICK_MS)
    return () => clearInterval(handle)
  }, [isGenerating, caseId, appendThought])

  return thoughts
}

/**
 * Resolve the active stage id from agent state. `current_stage` is the direct
 * signal when present; otherwise we fall back to the first stage not yet in
 * `completed_stages` (the reliably-propagated node-boundary signal). `stages`
 * must be the stage list of the case's own order type (default: OIO).
 */
export function deriveActiveStage(
  currentStage: string | null | undefined,
  completedStages: string[] | undefined,
  stages: readonly WorkflowStageDefinition[] = OIO_STAGES,
): string | null {
  if (currentStage) return currentStage
  const completed = new Set(completedStages ?? [])
  return stages.find((s) => !completed.has(s.id))?.id ?? null
}
